import { useMemo } from "react";
import { DataTable } from "@/components/DataTable";
import { createUnitColumns, UNIT_COLUMN_LABELS, UNIT_COLUMN_TYPES, UNIT_ENUM_VALUES } from "@/lib/table/unit-columns";
import type { Unit } from "@/lib/types/unit";
import { Users } from "lucide-react";
import type { FilterFn } from "@tanstack/react-table";
import { useDataStore } from "@/lib/store/data-store";
import { getDescriptorSearchText } from "@/lib/utils/search-normalize";

export function UnitsTable() {
  const columns = useMemo(() => createUnitColumns(), []);
  const { divisions } = useDataStore();

  // Create divisions enum values mapping (descriptor -> name)
  const divisionsEnumValues = useMemo(() => {
    const mapping: Record<string, string> = {};
    (divisions || []).forEach((div) => {
      mapping[div.name] = div.descriptor;
    });
    return mapping;
  }, [divisions]);

  // Merge divisions enum values with existing UNIT_ENUM_VALUES
  const enumValues = useMemo(() => {
    return {
      ...UNIT_ENUM_VALUES,
      divisions: divisionsEnumValues,
    };
  }, [divisionsEnumValues]);

  // Global filter function for units
  const globalFilterFn: FilterFn<Unit> = (row, columnId, filterValue: string) => {
    const search = filterValue.toLowerCase();
    const unit = row.original;
    return (
      unit.name.toLowerCase().includes(search) ||
      getDescriptorSearchText(unit.descriptorName).toLowerCase().includes(search)
    );
  };

  // Data parser for units API response
  const dataParser = (data: any): Unit[] => {
    // Handle both array and object with units property
    return Array.isArray(data) ? data : (data.units || []);
  };

  // Initial column visibility - hide these columns by default
  const initialColumnVisibility = useMemo(() => ({
    id: false,
    descriptorName: false,
    weaponDescriptorName: false,
    factoryDescriptor: false,
    formation: false,
    infoPanelType: false,
    movementType: false,
    occupiableTerrains: false,
    xpBonuses: false,
    isSpecialForces: false,
    isSellable: false,
  }), []);

  // Initial column order - first columns should be: Name, Coalition, Country, Divisions, CP, Specialities
  const initialColumnOrder = useMemo(() => [
    // Priority columns (first 6)
    "name",
    "coalition",
    "country",
    "divisions",
    "commandPoints",
    "specialities",
    // Then all other columns
    "id",
    "descriptorName",
    "weaponDescriptorName",
    "factoryDescriptor",
    "formation",
    "infoPanelType",
    "frontArmor",
    "sideArmor",
    "rearArmor",
    "topArmor",
    "era",
    "maxDamage",
    "speed",
    "roadSpeed",
    "rotationTime",
    "movementType",
    "occupiableTerrains",
    "optics",
    "airOptics",
    "lowAltOptics",
    "opticsRange",
    "airOpticsRange",
    "lowAltOpticsRange",
    "stealth",
    "ecm",
    "fuel",
    "fuelMove",
    "supply",
    "advancedDeployment",
    "agility",
    "flyingAltitude",
    "travelTime",
    "dangerousness",
    "bombStrategy",
    "xpBonuses",
    "isCommand",
    "isSpecialForces",
    "isSellable",
  ], []);

  return (
    <DataTable<Unit>
      columns={columns}
      columnLabels={UNIT_COLUMN_LABELS}
      columnTypes={UNIT_COLUMN_TYPES}
      enumValues={enumValues}
      apiEndpoint="/api/units"
      storagePrefix="units"
      title="Units"
      icon={<Users className="h-6 w-6" />}
      description="Browse and search through all available units"
      globalFilterFn={globalFilterFn}
      emptyMessage="No units available"
      emptyFilteredMessage="No units found matching your filters"
      itemName="units"
      dataParser={dataParser}
      initialColumnVisibility={initialColumnVisibility}
      initialColumnOrder={initialColumnOrder}
    />
  );
}
